import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; 
import { useAuth } from '../contexts/AuthContext';
import DateInput from '../components/DateInput';
import { buildIndividualSettleBlockAlert } from '../utils/settleBlockMessages';

// ⬇️ [모바일 고정 해제 1/3] baseStyles.filtersContainer.position을 'relative'로 변경
const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', // ⬅️ 모바일 기본값 (고정 해제)
    top: 0, 
    zIndex: 10,
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  searchInput: { padding: '8px', fontSize: '0.9rem', width: '160px' },
  searchButton: { padding: '8px 12px', fontSize: '0.9rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  
  // --- 검색 결과 테이블 ---
  resultsContainer: { marginTop: '20px' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px',
    paddingLeft: '5px' 
  },
  table: { width: '100%', minWidth: '600px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem' },
  trClickable: { cursor: 'pointer' }, 

  // --- 하단 상세 정산 영역 ---
  detailsContainer: {
    marginTop: '30px',
    padding: '20px',
    border: '1px solid #007bff', 
    borderRadius: '8px',
    background: '#fff',
  },
  detailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #eee',
    paddingBottom: '10px',
    marginBottom: '20px'
  },
  printButton: { padding: '8px 12px', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  
  // --- 상세 정보 테이블 ---
  infoTable: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px' },
  infoTh: { border: '1px solid #ddd', padding: '10px', background: '#f9f9f9', textAlign: 'right', width: '150px', fontWeight: 'bold' },
  infoTd: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' },

  // --- 정산 처리 영역 ---
  settleSection: { marginTop: '20px', padding: '15px', background: '#fff9e6', border: '1px solid #ffc107', borderRadius: '5px' },
  settleInput: { padding: '8px', fontSize: '1rem', width: '200px', marginRight: '10px' },
  settleAmount: { fontSize: '1.2rem', fontWeight: 'bold', color: '#d9534f' },
  settleButton: { padding: '12px 20px', fontSize: '1rem', backgroundColor: '#d9534f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '15px' },
};


const LeaveSettleIndividualPage = () => {
  const { user } = useAuth(); 

  // --- 1. 상태(State) 정의 --- (변경 없음)
  // ⬇️ [모바일 고정 해제 2/3] windowWidth state 추가
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    name: '',
    rrn: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [reportData, setReportData] = useState(null); 
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [endDate, setEndDate] = useState(''); 
  const [settleLoading, setSettleLoading] = useState(false);
  const [settleMessage, setSettleMessage] = useState('');
  const [settleError, setSettleError] = useState('');
  const [settledWarning, setSettledWarning] = useState(''); // 정산된 참여자 경고 메시지

  // --- 2. 동적 스타일 적용 (상단 고정) ---
  // ⬇️ [모바일 고정 해제] windowWidth useEffect 추가
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⬇️ [모바일 고정 해제 3/3] 동적 styles 변수 (position 속성 추가)
  const styles = {
    ...baseStyles,
    filtersContainer: {
      ...baseStyles.filtersContainer,
      // 1024px 초과 (데스크탑)일 때만 'sticky'로 변경하고 top: 60px
      position: windowWidth > 1024 ? 'sticky' : 'relative',
      top: windowWidth > 1024 ? '60px' : '0px', 
    }
  };
  
  // (인쇄 스타일 적용 - 변경 없음)
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
      @media print {
        .sidebar, .header { display: none !important; }
        .no-print { display: none !important; }
        .printable-area { border: none !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
        body, #root, .layout, .main-content, .page-content { background: #fff !important; margin: 0 !important; padding: 0 !important; }
        .detailsContainer { break-inside: avoid; } 
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // --- 3. 핸들러: 필터 변경 --- (변경 없음)
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // --- 4. 핸들러: 검색 (상단) --- (변경 없음)
  const handleSearch = useCallback(async () => {
    if (!filters.year) {
      setSearchError('연도는 필수입니다.');
      return;
    }
    setSearchLoading(true);
    setSearchError('');
    setSettledWarning('');
    setSearchResults([]);
    setSelectedParticipant(null); 
    setReportData(null);
    try {
      const res = await api.get('/participants/search-settle', { 
        params: filters 
      });
      setSearchResults(res.data);
      
      // 정산된 참여자가 있는지 확인
      const settledParticipants = res.data.filter(p => p.status === 'ended');
      if (settledParticipants.length > 0) {
        const names = settledParticipants.map(p => p.name).join(', ');
        setSettledWarning(`⚠️ 주의: 검색 결과에 이미 정산된 참여자가 포함되어 있습니다. (${names})`);
      }
    } catch (err) {
      setSearchError(err.response?.data?.message || '검색에 실패했습니다.');
    } finally {
      setSearchLoading(false);
    }
  }, [filters]);

  // --- 5. 핸들러: 참여자 선택 (상세 조회) --- (변경 없음)
  const handleSelectParticipant = useCallback(async (participant) => {
    setSelectedParticipant(participant);
    setReportLoading(true);
    setReportError('');
    setReportData(null);
    setSettleMessage('');
    setSettleError('');
    
    // 정산된 참여자인지 확인
    if (participant.status === 'ended') {
      setSettledWarning(`⚠️ 경고: "${participant.name}" 참여자는 이미 정산이 완료된 상태입니다.`);
    } else {
      setSettledWarning('');
    }
    
    try {
      const res = await api.get(`/report/individual/${participant.id}`, {
        params: { year: filters.year }
      });
      setReportData(res.data);
      setEndDate(res.data.participantInfo.endDate || ''); 
    } catch (err) {
      setReportError(err.response?.data?.message || '참여자 상세 정보 로드에 실패했습니다.');
    } finally {
      setReportLoading(false);
    }
  }, [filters.year]);

  // --- 6. 핸들러: 정산 실행 (하단) --- (변경 없음)
  const handleSettleSubmit = async () => {
    if (!reportData || !selectedParticipant) return;
    if (!endDate) {
      setSettleError('참여 종료일을 반드시 입력해야 합니다.');
      return;
    }
    
    // 정산된 참여자인지 확인
    if (selectedParticipant.status === 'ended') {
      const confirmMessage = `⚠️ 경고: "${selectedParticipant.name}" 참여자는 이미 정산이 완료된 상태입니다.\n\n중복 정산을 진행하시겠습니까?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    // 참여 기간 검증
    try {
      const validationRes = await api.post('/settle/validate-period', {
        participantId: selectedParticipant.id,
        year: filters.year,
        endDate: endDate
      });

      if (validationRes.data.hasInvalidData) {
        alert(buildIndividualSettleBlockAlert(validationRes.data));
        return;
      }
    } catch (err) {
      console.error('참여 기간 검증 실패:', err);
      alert('참여 기간 검증 중 오류가 발생했습니다. 정산을 진행할 수 없습니다.');
      return; // 검증 실패 시에도 정산 진행 불가
    }

    if (!window.confirm(`[${selectedParticipant.name}] 님의 참여를 종료하고, 미정산 연차(${reportData.leaveSummary.remainingUnpaidHours}시간)를 정산 처리합니다. 정말 실행하시겠습니까?`)) {
      return;
    }
    
    setSettleLoading(true);
    setSettleMessage('');
    setSettleError('');

    try {
      const res = await api.post('/settle/individual', {
        participantId: selectedParticipant.id,
        year: filters.year,
        endDate: endDate, 
        settleHours: reportData.leaveSummary.remainingUnpaidHours 
      });
      setSettleMessage(res.data.message);
      handleSelectParticipant(selectedParticipant); 
    } catch (err) {
      setSettleError(err.response?.data?.message || '정산 처리에 실패했습니다.');
      if (err.response?.data?.hasPendingLeave || err.response?.data?.hasPeriodViolation) {
        alert(buildIndividualSettleBlockAlert(err.response.data));
      }
    } finally {
      setSettleLoading(false);
    }
  };
  
  // --- 7. 핸들러: 인쇄 --- (변경 없음)
  const handlePrint = () => {
    window.print();
  };
  
  // (자동 계산 - 변경 없음)
  const settleAmount = reportData 
    ? (reportData.leaveSummary.remainingUnpaidHours * reportData.participantInfo.Project.leavePayRate)
    : 0;

  return (
    <div>
      <h2 className="no-print">🧑‍💻 연차 정산 (개별)</h2>

      {/* 1. 검색 필터 (상단 고정) (변경 없음) */}
      <div style={styles.filtersContainer} className="no-print">
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>*연도</span>
            <input
              type="number"
              name="year"
              style={styles.searchInput}
              value={filters.year}
              onChange={handleFilterChange}
              min="2020"
              step="1"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input
              type="text"
              name="name"
              style={styles.searchInput}
              value={filters.name}
              onChange={handleFilterChange}
              onKeyPress={handleFilterKeyPress}
              placeholder="홍길동"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>주민번호 (앞6자리)</span>
            <input
              type="text"
              name="rrn"
              style={styles.searchInput}
              value={filters.rrn}
              onChange={handleFilterChange}
              onKeyPress={handleFilterKeyPress}
              placeholder="500101"
              maxLength={6}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>&nbsp;</span>
            <button style={styles.searchButton} onClick={handleSearch} disabled={searchLoading}>
              {searchLoading ? '검색중...' : '🔍 검색'}
            </button>
          </div>
        </div>
      </div>

      {/* 2. 검색 결과 테이블 (변경 없음) */}
      <div style={styles.resultsContainer} className="no-print">
        <div style={styles.countDisplay}>
          {searchLoading ? '검색 중...' : `검색된 인원: 총 ${searchResults.length} 명`}
        </div>
        {searchError && <p style={{ color: 'red' }}>{searchError}</p>}
        {settledWarning && <p style={{ color: '#ff9800', fontWeight: 'bold', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '10px' }}>{settledWarning}</p>}
        
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>사업명</th>
              <th style={styles.th}>성명</th>
              <th style={styles.th}>주민등록번호</th>
              <th style={styles.th}>근무장소</th>
            </tr>
          </thead>
          <tbody>
            {searchResults.length > 0 ? (
              searchResults.map(p => (
                <tr 
                  key={p.id} 
                  style={{...styles.trClickable, backgroundColor: selectedParticipant?.id === p.id ? '#e6f7ff' : 'transparent', ':hover': { backgroundColor: '#f0f0f0' }}}
                  onClick={() => handleSelectParticipant(p)}
                >
                  <td style={styles.td}>{p.Project.projectName}</td>
                  <td style={{...styles.td, textAlign: 'center'}}>{p.name}</td>
                  <td style={{...styles.td, textAlign: 'center'}}>{p.rrn.substring(0, 8)}...</td>
                  <td style={styles.td}>{p.workplace}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{...styles.td, textAlign: 'center'}}>
                  {searchLoading ? '검색 중...' : '검색 결과가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 3. 상세 정산 영역 (변경 없음) */}
      {reportLoading && <p style={{ marginTop: '20px' }}>상세 현황을 불러오는 중...</p>}
      {reportError && <p style={{ marginTop: '20px', color: 'red' }}>{reportError}</p>}
      {selectedParticipant && selectedParticipant.status === 'ended' && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', border: '2px solid #ff9800', borderRadius: '5px' }}>
          <p style={{ color: '#ff9800', fontWeight: 'bold', margin: 0, fontSize: '1.1rem' }}>
            ⚠️ 경고: 이 참여자는 이미 정산이 완료된 상태입니다. 정산을 다시 진행하면 중복 정산이 될 수 있습니다.
          </p>
        </div>
      )}
      
      {reportData && selectedParticipant && (
        <>
          {/* 3-1. 인쇄될 영역 */}
          <div style={styles.detailsContainer} className="printable-area detailsContainer">
            <div style={styles.detailsHeader}>
              <h3 style={{ margin: 0 }}>
                [{selectedParticipant.name}] 님 ({filters.year}년) 상세 현황 보고서
              </h3>
              <button style={styles.printButton} className="no-print" onClick={handlePrint}>
                🖨️ 인쇄
              </button>
            </div>

            {/* --- 1. 연차 현황 (조회) --- */}
            <h4>1. 연차 현황 (조회)</h4>
            <table style={styles.infoTable}>
              <tbody>
                <tr>
                  <th style={styles.infoTh}>참여 사업</th>
                  <td style={styles.infoTd}>{reportData.participantInfo.Project.projectName}</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>참여자 성명</th>
                  <td style={styles.infoTd}>{reportData.participantInfo.name}</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>주민등록번호</th>
                  <td style={styles.infoTd}>{reportData.participantInfo.rrn}</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>은행 / 계좌번호</th>
                  <td style={styles.infoTd}>
                    {(reportData.participantInfo.bankName || '') ? `${reportData.participantInfo.bankName} / ${reportData.participantInfo.bankAccount || ''}` : '(미등록)'}
                  </td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>참여 시작일</th>
                  <td style={styles.infoTd}>{reportData.participantInfo.startDate}</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>기존 참여 종료일</th>
                  <td style={styles.infoTd}>{reportData.participantInfo.endDate || '(미지정)'}</td>
                </tr>
                <tr style={{borderTop: '2px solid #007bff'}}>
                  <th style={styles.infoTh}>(A) 총 발생 연차</th>
                  <td style={styles.infoTd}>{reportData.leaveSummary.totalGeneratedHours} 시간</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>(B) 총 사용 연차</th>
                  <td style={styles.infoTd}>{reportData.leaveSummary.cumulativeUsedHours} 시간</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>(C) 총 기지급 연차</th>
                  <td style={styles.infoTd}>{reportData.leaveSummary.totalPaidOutHours} 시간</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>(D) 잔여 연차 (A - B)</th>
                  <td style={styles.infoTd}>{reportData.leaveSummary.remainingHours} 시간</td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>(E) 미정산 연차 (D - C)</th>
                  <td style={{...styles.infoTd, fontWeight: 'bold', color: '#d9534f'}}>
                    {reportData.leaveSummary.remainingUnpaidHours} 시간
                  </td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>연차 미발생월</th>
                  <td style={styles.infoTd}>
                    {reportData.leaveSummary.nonAccrualMonths || '(없음)'}
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* --- 2. 정산 내역 (인쇄용) --- */}
            <h4>2. 정산 내역</h4>
            <table style={styles.infoTable}>
              <tbody>
                <tr>
                  <th style={styles.infoTh}>신규 참여 종료일</th>
                  <td style={styles.infoTd}>{endDate || '(미지정)'}</td>
                </tr>
                <tr style={{borderTop: '2px solid #d9534f'}}>
                  <th style={styles.infoTh}>연차 정산액 (예상)</th>
                  <td style={{...styles.infoTd, fontWeight: 'bold', color: '#d9534f'}}>
                    {settleAmount.toLocaleString()} 원
                  </td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>산출 근거</th>
                  <td style={styles.infoTd}>
                    (미정산 {reportData.leaveSummary.remainingUnpaidHours}h * 수당 {reportData.participantInfo.Project.leavePayRate.toLocaleString()}원)
                  </td>
                </tr>
                <tr>
                  <th style={styles.infoTh}>지급 계좌</th>
                  <td style={styles.infoTd}>
                    {(reportData.participantInfo.bankName || '') ? `${reportData.participantInfo.bankName} / ${reportData.participantInfo.bankAccount || ''}` : '(미등록)'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 3-2. 정산 처리 (입력) - 인쇄 제외 */}
          <div className="no-print" style={{marginTop: '20px'}}>
            <h4>3. 정산 실행 (입력)</h4>
            <div style={styles.settleSection}>
              <p style={{marginTop: 0, fontWeight: 'bold'}}>
                ⚠️ 아래 버튼을 누르면 해당 참여자의 상태가 '종료'로 변경되고 연차가 정산됩니다.
              </p>
              
              <div style={styles.filterItem}>
                <label style={styles.filterLabel} htmlFor="endDateInput">
                  (1) 참여 종료일 (신규/덮어쓰기)
                </label>
                <DateInput value={endDate} onChange={setEndDate} inputStyle={styles.settleInput} />
              </div>
              
              <div style={{...styles.filterItem, marginTop: '20px'}}>
                <button 
                  style={styles.settleButton} 
                  onClick={handleSettleSubmit} 
                  disabled={settleLoading || !endDate}
                >
                  {settleLoading ? '처리 중...' : '💾 정산 실행 및 참여 종료 처리'}
                </button>
                {settleMessage && <p style={{ color: 'green', fontWeight: 'bold' }}>{settleMessage}</p>}
                {settleError && <p style={{ color: 'red', fontWeight: 'bold' }}>{settleError}</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LeaveSettleIndividualPage;